import {resolveIndividualIdentityKey} from '@sollapay/domain';
import {PartyType} from '@sollapay/enums';
import {Button, Divider, Icon, Table, Typography, nodeColumn} from '@sollapay/ui/components';
import {useCallback, useMemo, type FC} from 'react';
import {useTranslation} from 'react-i18next';

import {
  BeneficiaryActionsCell,
  BeneficiaryAddressCell,
  BeneficiaryDateCell,
  BeneficiaryIdentifierCell,
  BeneficiaryNameCell,
  BeneficiaryRoleCell
} from './BeneficiaryTableCells';
import {useBeneficiaryDraftForm} from './hooks/useBeneficiaryDraftForm';

import type {EntityTypeCopyMap} from './BeneficiaryTableCells';
import type {BeneficiaryRow, DraftBeneficiary} from './types/beneficiary-draft.types';
import type {PartyRole} from '@sollapay/enums';

const ROW_CLASS_BY_STATE = {
  deal: 'bg-background-surface',
  manual:
    'bg-[color-mix(in_oklab,var(--brand-primary)_1.6%,var(--background-surface))] hover:bg-[color-mix(in_oklab,var(--brand-primary)_3.5%,var(--background-surface))]',
  draft: 'items-start bg-background-secondary [&>td]:py-4 [&>td]:align-top'
} as const;
const ROW_ACCENT_CLASS_BY_STATE = {
  deal: undefined,
  manual: 'bg-brand-primary/[55%]',
  draft: 'bg-brand-primary'
} as const;

interface BeneficiariesTableProps {
  beneficiaries: BeneficiaryRow[];
  onAddParty?: () => void;
  onStartEdit?: (row: BeneficiaryRow) => void;
  onRemoveRow?: (partyId: string) => void;
  addableRoles?: PartyRole[];
  isAddingParty?: boolean;
  isConfirmingDraft?: boolean;
  onConfirmDraft?: (draft: DraftBeneficiary) => void;
  onCancelDraft?: () => void;
  editingPartyId?: string | null;
  isConfirmingEdit?: boolean;
  onConfirmEdit?: (partyId: string, values: DraftBeneficiary) => void;
  onCancelEdit?: () => void;
}

export const BeneficiariesTable: FC<BeneficiariesTableProps> = ({
  beneficiaries,
  onAddParty,
  onStartEdit,
  onRemoveRow,
  addableRoles,
  isAddingParty,
  isConfirmingDraft,
  onConfirmDraft,
  onCancelDraft,
  editingPartyId,
  isConfirmingEdit,
  onConfirmEdit,
  onCancelEdit
}) => {
  const {t} = useTranslation('trustAccounts');

  const {rows, erroredFields, updateField, attemptSave, handleDraftKeyDown, errorMessage} =
    useBeneficiaryDraftForm({
      beneficiaries,
      isAddingParty,
      editingPartyId,
      onConfirmDraft,
      onConfirmEdit,
      onCancelDraft,
      onCancelEdit
    });

  const draftRoleOptions = useMemo(
    () => (addableRoles ?? []).map(role => ({value: role, label: t(`partyRole.${role}`)})),
    [addableRoles, t]
  );

  const entityTypeCopy: EntityTypeCopyMap = useMemo(
    () => ({
      [PartyType.PERSON]: {
        toggleLabel: t('view.beneficiaries.entityType.person'),
        nameLabel: t('common:fields.fullName.label'),
        namePlaceholder: t('common:fields.fullName.placeholder'),
        idLabel: t('common:fields.nationalId.label'),
        idPlaceholder: t('common:fields.nationalId.placeholder'),
        idHint: t('common:fields.nationalId.hint'),
        dateLabel: t('common:fields.dateOfBirth.label')
      },
      [PartyType.ORGANIZATION]: {
        toggleLabel: t('view.beneficiaries.entityType.organization'),
        nameLabel: t('common:fields.fullName.label_organization'),
        namePlaceholder: t('common:fields.fullName.placeholder_organization'),
        idLabel: t('common:fields.companyId.label'),
        idPlaceholder: t('common:fields.companyId.placeholder'),
        idHint: t('common:fields.companyId.hint'),
        dateLabel: t('common:fields.incorporationDate.label')
      }
    }),
    [t]
  );

  const dealCount = beneficiaries.filter(row => row.source === 'deal').length;
  const manualCount = beneficiaries.length - dealCount;

  const rowsByIdentityKey = useMemo(() => {
    const groups = new Map<string, BeneficiaryRow[]>();
    for (const b of beneficiaries) {
      const key = resolveIndividualIdentityKey(b);
      if (!key) continue;
      groups.set(key, [...(groups.get(key) ?? []), b]);
    }
    return groups;
  }, [beneficiaries]);

  const isDuplicateIdentifier = (row: BeneficiaryRow): boolean => {
    const key = resolveIndividualIdentityKey(row);
    if (!key) return false;
    return (rowsByIdentityKey.get(key) ?? []).some(b => b.partyId !== row.partyId);
  };

  const columns = useMemo(
    () => [
      nodeColumn<BeneficiaryRow>({
        id: 'name',
        header: t('view.beneficiaries.columns.name'),
        accessorFn: row => row.name,
        cell: row => (
          <BeneficiaryNameCell
            row={row}
            entityTypeCopy={entityTypeCopy}
            onFieldChange={updateField}
            onKeyDown={handleDraftKeyDown(row)}
            erroredFields={erroredFields}
            errorMessage={errorMessage}
          />
        ),
        width: 'min'
      }),
      nodeColumn<BeneficiaryRow>({
        id: 'role',
        header: t('view.beneficiaries.columns.role'),
        accessorFn: row => row.role,
        cell: row => (
          <BeneficiaryRoleCell
            row={row}
            isManual={row.source === 'manual'}
            roleOptions={draftRoleOptions}
            onFieldChange={updateField}
            erroredFields={erroredFields}
            errorMessage={errorMessage}
          />
        ),
        width: 100,
        maxWidth: 100
      }),
      nodeColumn<BeneficiaryRow>({
        id: 'idNumber',
        header: t('view.beneficiaries.columns.idNumber'),
        accessorFn: row => row.idNumber,
        cell: row => (
          <BeneficiaryIdentifierCell
            row={row}
            entityTypeCopy={entityTypeCopy}
            isDuplicate={isDuplicateIdentifier(row)}
            onFieldChange={updateField}
            onKeyDown={handleDraftKeyDown(row)}
            erroredFields={erroredFields}
            errorMessage={errorMessage}
          />
        ),
        width: 'min'
      }),
      nodeColumn<BeneficiaryRow>({
        id: 'date',
        header: t('view.beneficiaries.columns.date'),
        accessorFn: row => row.date,
        cell: row => (
          <BeneficiaryDateCell
            row={row}
            entityTypeCopy={entityTypeCopy}
            onFieldChange={updateField}
            erroredFields={erroredFields}
            errorMessage={errorMessage}
          />
        ),
        width: 130,
        maxWidth: 130
      }),
      nodeColumn<BeneficiaryRow>({
        id: 'address',
        header: t('view.beneficiaries.columns.address'),
        accessorFn: row => row.address,
        cell: row => (
          <BeneficiaryAddressCell
            row={row}
            onFieldChange={updateField}
            onKeyDown={handleDraftKeyDown(row)}
            erroredFields={erroredFields}
            errorMessage={errorMessage}
          />
        ),
        width: 180,
        maxWidth: 180
      }),
      nodeColumn<BeneficiaryRow>({
        id: 'actions',
        header: '',
        width: 'min',
        cell: row => (
          <BeneficiaryActionsCell
            row={row}
            isManual={row.source === 'manual'}
            isConfirmingDraft={isConfirmingDraft}
            isConfirmingEdit={isConfirmingEdit}
            onSave={attemptSave}
            onCancelDraft={onCancelDraft}
            onCancelEdit={onCancelEdit}
            onStartEdit={onStartEdit}
            onRemoveRow={onRemoveRow}
          />
        )
      })
    ],
    // formValues excluded — rebuilding columns per keystroke would remount inputs and drop focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      beneficiaries,
      onStartEdit,
      onRemoveRow,
      draftRoleOptions,
      entityTypeCopy,
      erroredFields,
      isConfirmingDraft,
      onConfirmDraft,
      onCancelDraft,
      isConfirmingEdit,
      onConfirmEdit,
      onCancelEdit,
      t
    ]
  );

  const rowState = useCallback((row: BeneficiaryRow): keyof typeof ROW_CLASS_BY_STATE => {
    if (row.isDraft || row.isEditing) return 'draft';
    return row.source;
  }, []);

  const getRowClassName = useCallback(
    (row: BeneficiaryRow) => ROW_CLASS_BY_STATE[rowState(row)],
    [rowState]
  );

  const getRowAccentClassName = useCallback(
    (row: BeneficiaryRow) => ROW_ACCENT_CLASS_BY_STATE[rowState(row)],
    [rowState]
  );

  const isDealSourcedRow = useCallback(
    (row: BeneficiaryRow) => !row.isDraft && row.source === 'deal',
    []
  );

  const isRowInProgress = isAddingParty || !!editingPartyId;

  const footer = onAddParty ? (
    <div>
      <Divider />
      <div className="flex items-center justify-between px-6 py-4">
        <Button
          variant="linkColor"
          size="sm"
          icon={<Icon name="add" />}
          onClick={onAddParty}
          disabled={isRowInProgress}
        >
          {t('view.beneficiaries.addParty')}
        </Button>
        <Typography size="xs" color="tertiary">
          {t('view.beneficiaries.rowCounter', {dealCount, manualCount})}
        </Typography>
      </div>
    </div>
  ) : undefined;

  return (
    <Table
      columns={columns}
      data={rows}
      footer={footer}
      getRowClassName={getRowClassName}
      getRowAccentClassName={getRowAccentClassName}
      isRowPinned={isDealSourcedRow}
    />
  );
};

export type {BeneficiariesTableProps};
